import type { IAction } from "@bbj-types/action";
import { tz } from "@date-fns/tz";
import { Stack } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { getDateDeltaDays } from "@root/utils/date/delta";
import { useI18nContext } from "@root/utils/organisation";
import { AlertCollapse } from "@surfaces/AlertCollapse";
import { startOfDay } from "date-fns";
import { useMemo } from "react";
import { type Control, useWatch } from "react-hook-form";
import type { IModalActionForm, IModalActionResolver } from "../logics/types";
import { hasConvocation } from "../logics/utils";

export const getMessageFromActionDate = (actionDate: Date, status: IAction["status"], timeZone: string): string => {
  if (status !== "todo") {
    return "Pour qu'une convocation soit envoyée au candidat pour ce rendez-vous, le statut de l'action doit être \"à réaliser\".";
  }

  const deltaDays = getDateDeltaDays(actionDate, startOfDay(new Date(), { in: tz(timeZone) }), timeZone);
  if (deltaDays > 30) {
    return "Le rendez-vous est dans plus d’un mois, la convocation sera envoyée 30 jours seulement avant le rendez-vous.";
  }
  if (deltaDays > 6) {
    return "Une convocation sera envoyée dès demain.";
  }
  return "Le rendez-vous est dans moins de 7 jours, aucune convocation ne sera envoyée.";
};

export interface IModalActionFormConvocationProps {
  control: Control<IModalActionForm, void, IModalActionResolver>;
  hidden?: boolean;
}

export const ModalActionFormConvocation = ({ control, hidden }: IModalActionFormConvocationProps) => {
  const { user } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const [date, type, candidate, status, contactChannel] = useWatch({
    control,
    name: ["date", "type", "candidate", "status", "contactChannel"],
  });
  const displayConvocationText =
    candidate &&
    (status === "todo" || ((status === "missed" || status === "done") && contactChannel)) &&
    hasConvocation(candidate, user.organisation.features.team.active, type);
  const message = useMemo(
    () => (date && displayConvocationText ? getMessageFromActionDate(date, status, timeZone) : ""),
    [date, status, displayConvocationText]
  );

  if (hidden || !displayConvocationText || !message) {
    return null;
  }
  return (
    <Stack>
      <AlertCollapse open={true} severity="info">
        {message}
      </AlertCollapse>
    </Stack>
  );
};
